import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE as API } from '../config';
import { Mail, Lock, Play, ArrowRight, User, Building2, CheckCircle2, Truck, Network, Eye, UserCircle, Shield, Users as UsersIcon } from 'lucide-react';
import { isDemoMode, DEMO_CREDENTIALS } from '../components/DemoBanner';

const demo = isDemoMode();

export default function Login() {
  const [searchParams] = useSearchParams();
  const demoParam = searchParams.get('demo');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [demoType, setDemoType] = useState(demoParam || 'solo');
  const [demoRole, setDemoRole] = useState('admin');
  const { login, loginWithGoogle, setDemoViewMode, tryDemo } = useAuth();
  const navigate = useNavigate();

  // Tab state: null (sign-in default) | 'demo' | 'solo' | 'provider'
  const initialTab = demoParam ? 'demo' : null;
  const [activeTab, setActiveTab] = useState(initialTab);

  // Registration fields
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regOrg, setRegOrg] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');

  // Auto-trigger demo login from pricing page
  const [autoTriggered, setAutoTriggered] = useState(false);
  useEffect(() => {
    if (demoParam && !autoTriggered) {
      setAutoTriggered(true);
      setDemoType(demoParam);
      handleAutoDemoLogin(demoParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoParam]);

  const handleAutoDemoLogin = async (mode) => {
    setSubmitting(true);
    try {
      await tryDemo({ view: mode, role: 'admin' });
      navigate('/');
    } catch {
      toast.error('Demo login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      navigate(u.is_developer && u.is_platform_operator ? '/dev-hub' : '/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryDemo = async () => {
    setSubmitting(true);
    try {
      await tryDemo({ view: demoType, role: demoRole });
      navigate('/');
    } catch (err) {
      toast.error('Demo login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e, accountType) => {
    e.preventDefault();
    if (regPassword !== regConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (regPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, {
        name: regName, email: regEmail, password: regPassword,
        org_name: regOrg || undefined, account_type: accountType,
      });
      toast.success('Account created! Signing you in...');
      await login(regEmail, regPassword);
      navigate('/welcome');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Non-demo mode: simple login card
  if (!demo) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-3">
              <img src="/logo.png" alt="Logo" className="w-14 h-14 mx-auto" />
            </div>
            <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
            <CardDescription>Sign in to your claims management account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input id="login-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" required data-testid="login-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-blue-500 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input id="login-password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" required data-testid="login-password-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600" disabled={submitting} data-testid="login-submit-btn">
                {submitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-300" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-500">Or continue with</span></div>
            </div>
            <Button variant="outline" className="w-full" onClick={loginWithGoogle} data-testid="google-login-btn">
              <GoogleIcon />
              Sign in with Google
            </Button>
            <p className="text-center mt-4 text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-500 hover:underline font-medium" data-testid="register-link">Sign up</Link>
              {' '}&middot;{' '}
              <Link to="/pricing" className="text-teal-600 hover:underline font-medium" data-testid="see-pricing-link">See Pricing</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tabs config
  const tabs = [
    { id: 'demo', label: 'Try Demo', icon: Play, color: 'amber' },
    { id: 'solo', label: 'Solo Sign Up', icon: Truck, color: 'teal' },
    { id: 'provider', label: 'Provider Sign Up', icon: Network, color: 'blue' },
  ];

  const colorMap = {
    amber: { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', ring: 'ring-amber-500', tabActive: 'border-amber-500 text-amber-700 bg-amber-50/60', tabInactive: 'text-gray-500 hover:text-amber-600 hover:bg-amber-50/40' },
    teal: { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', light: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-600', ring: 'ring-teal-500', tabActive: 'border-teal-500 text-teal-700 bg-teal-50/60', tabInactive: 'text-gray-500 hover:text-teal-600 hover:bg-teal-50/40' },
    blue: { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', ring: 'ring-blue-500', tabActive: 'border-blue-500 text-blue-700 bg-blue-50/60', tabInactive: 'text-gray-500 hover:text-blue-600 hover:bg-blue-50/40' },
  };

  const currentTab = tabs.find(t => t.id === activeTab);
  const c = colorMap[currentTab?.color || 'amber'];

  // Registration form (reused for solo & provider tabs)
  const RegistrationForm = ({ accountType, accentColor }) => {
    const ac = colorMap[accentColor];
    return (
      <div>
        <form onSubmit={(e) => handleRegister(e, accountType)} className="space-y-3">
          <div className="relative">
            <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input placeholder="Full Name" value={regName} onChange={(e) => setRegName(e.target.value)} className="pl-9 h-10 bg-white" required data-testid="register-name-input" />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input type="email" placeholder="Email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="pl-9 h-10 bg-white" required data-testid="register-email-input" />
          </div>
          <div className="relative">
            <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input placeholder="Organization Name (optional)" value={regOrg} onChange={(e) => setRegOrg(e.target.value)} className="pl-9 h-10 bg-white" data-testid="register-org-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input type="password" placeholder="Password (min 6)" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} className="pl-9 h-10 bg-white" required data-testid="register-password-input" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input type="password" placeholder="Confirm Password" value={regConfirm} onChange={(e) => setRegConfirm(e.target.value)} className="pl-9 h-10 bg-white" required data-testid="register-confirm-input" />
            </div>
          </div>
          <Button type="submit" className={`w-full h-11 text-base font-semibold text-white ${ac.bg} ${ac.hover}`} disabled={submitting} data-testid="register-submit-btn">
            <ArrowRight className="w-4 h-4 mr-2" />
            {submitting ? 'Creating Account...' : 'Start Free Trial'}
          </Button>
        </form>
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">Or sign up with</span></div>
        </div>
        <Button variant="outline" className="w-full h-10" onClick={loginWithGoogle} data-testid="google-register-btn">
          <GoogleIcon />
          Google
        </Button>
        <p className="text-center mt-3 text-xs text-gray-400">
          Already have an account?{' '}
          <button type="button" onClick={() => setActiveTab(null)} className="text-blue-500 hover:underline font-medium">Sign in</button>
        </p>
      </div>
    );
  };

  // Demo mode: tabbed layout
  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl" data-testid="login-card">
        {/* Header */}
        <div className="text-center mb-5">
          <img src="/logo.png" alt="Logo" className="w-12 h-12 mx-auto mb-2" />
          <h1 className="text-xl font-bold text-gray-900">CMS By All About Claims</h1>
          <p className="text-sm text-gray-500 mt-1">
            Explore the demo or start your free trial &middot;{' '}
            <Link to="/pricing" className="text-teal-600 hover:underline font-medium" data-testid="demo-see-pricing-link">See Pricing</Link>
          </p>
        </div>

        {/* Card with tabs */}
        <Card className="shadow-lg overflow-hidden" data-testid="login-tabbed-card">
          {/* Tab bar */}
          <div className="flex border-b border-gray-200 bg-gray-50/50">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const tc = colorMap[tab.color];
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(isActive ? null : tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 px-2 text-sm font-medium border-b-2 transition-all duration-200 ${
                    isActive ? `${tc.tabActive} border-b-2` : `border-transparent ${tc.tabInactive}`
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.id === 'demo' ? 'Demo' : tab.id === 'solo' ? 'Solo' : 'Provider'}</span>
                </button>
              );
            })}
          </div>

          {/* Tab content with transition */}
          <div className="relative">
            <div key={activeTab ?? 'signin'} className="animate-fadeSlideIn">
              <CardContent className="pt-5 pb-6">

                {/* ═══ DEFAULT: SIGN IN ═══ */}
                {activeTab === null && (
                  <div data-testid="signin-default-content">
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 h-10 bg-white" required data-testid="login-email-input" />
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-10 bg-white" required data-testid="login-password-input" />
                      </div>
                      <Button type="submit" className="w-full h-11 text-base font-semibold bg-teal-500 hover:bg-teal-600 text-white" disabled={submitting} data-testid="login-submit-btn">
                        {submitting ? 'Signing in...' : 'Sign In'}
                      </Button>
                    </form>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
                      <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">Or continue with</span></div>
                    </div>
                    <Button variant="outline" className="w-full h-10" onClick={loginWithGoogle} data-testid="google-login-btn">
                      <GoogleIcon />
                      Sign in with Google
                    </Button>
                    <div className="flex items-center justify-between mt-4">
                      <Link to="/forgot-password" className="text-xs text-blue-500 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                      <p className="text-xs text-gray-400">
                        New here? Click <button type="button" onClick={() => setActiveTab('solo')} className="text-teal-600 hover:underline font-medium">Solo</button> or <button type="button" onClick={() => setActiveTab('provider')} className="text-blue-600 hover:underline font-medium">Provider</button> above
                      </p>
                    </div>
                  </div>
                )}

                {/* ═══ DEMO TAB ═══ */}
                {activeTab === 'demo' && (
                  <div data-testid="demo-tab-content">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                      {/* Solo demo card */}
                      <button
                        type="button"
                        onClick={() => setDemoType('solo')}
                        className={`text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          demoType === 'solo' ? 'border-teal-400 bg-teal-50/50 ring-1 ring-teal-200' : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/30'
                        }`}
                        data-testid="demo-solo-card"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-1.5 rounded-md ${demoType === 'solo' ? 'bg-teal-100' : 'bg-gray-100'}`}>
                            <Truck className={`w-4 h-4 ${demoType === 'solo' ? 'text-teal-600' : 'text-gray-500'}`} />
                          </div>
                          <span className="font-semibold text-sm text-gray-900">Solo</span>
                          <span className="text-xs text-gray-400">$39/mo</span>
                        </div>
                        <ul className="space-y-1 text-xs text-gray-600">
                          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-teal-500 shrink-0" />Single company dashboard</li>
                          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-teal-500 shrink-0" />Claims, reports & intake forms</li>
                          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-teal-500 shrink-0" />Full document management</li>
                        </ul>
                      </button>
                      {/* Provider demo card */}
                      <button
                        type="button"
                        onClick={() => setDemoType('provider')}
                        className={`text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          demoType === 'provider' ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-200' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
                        }`}
                        data-testid="demo-provider-card"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-1.5 rounded-md ${demoType === 'provider' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <Network className={`w-4 h-4 ${demoType === 'provider' ? 'text-blue-600' : 'text-gray-500'}`} />
                          </div>
                          <span className="font-semibold text-sm text-gray-900">Provider</span>
                          <span className="text-xs text-gray-400">$99/mo</span>
                        </div>
                        <ul className="space-y-1 text-xs text-gray-600">
                          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-500 shrink-0" />Multi-org dashboard</li>
                          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-500 shrink-0" />6 organizations, 6 companies</li>
                          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-500 shrink-0" />Cross-org reporting</li>
                        </ul>
                      </button>
                    </div>

                    {/* Role selector — pick which role to view the demo as */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">View Demo As</span>
                        <span className="text-[10px] text-gray-400">Each role unlocks different permissions</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2" data-testid="demo-role-selector">
                        {[
                          { id: 'viewer',  label: 'Viewer',  icon: Eye,        desc: 'Read-only' },
                          { id: 'member',  label: 'Member',  icon: UserCircle, desc: 'Create + edit' },
                          { id: 'manager', label: 'Manager', icon: UsersIcon,  desc: 'Team + claims' },
                          { id: 'admin',   label: 'Admin',   icon: Shield,     desc: 'Full access' },
                        ].map((r) => {
                          const isSel = demoRole === r.id;
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => setDemoRole(r.id)}
                              className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-lg border-2 transition-all duration-150 ${
                                isSel
                                  ? `${demoType === 'solo' ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-200' : 'border-blue-400 bg-blue-50 ring-1 ring-blue-200'}`
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                              data-testid={`demo-role-${r.id}`}
                              aria-pressed={isSel}
                            >
                              <r.icon className={`w-4 h-4 ${isSel ? (demoType === 'solo' ? 'text-teal-600' : 'text-blue-600') : 'text-gray-500'}`} />
                              <span className={`text-xs font-semibold ${isSel ? 'text-gray-900' : 'text-gray-700'}`}>{r.label}</span>
                              <span className="text-[10px] text-gray-400 leading-tight text-center">{r.desc}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <Button
                      className={`w-full h-11 text-base font-semibold text-white ${demoType === 'solo' ? 'bg-teal-500 hover:bg-teal-600' : 'bg-blue-500 hover:bg-blue-600'} transition-colors duration-200`}
                      onClick={handleTryDemo}
                      disabled={submitting}
                      data-testid="try-demo-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {submitting ? 'Loading Demo...' : `Try ${demoType === 'solo' ? 'Solo' : 'Provider'} Demo as ${demoRole.charAt(0).toUpperCase() + demoRole.slice(1)}`}
                    </Button>
                    <p className="text-center mt-3 text-xs text-gray-400">
                      Already have an account?{' '}
                      <button type="button" onClick={() => setActiveTab(null)} className="text-teal-600 hover:underline font-medium">Sign in</button>
                    </p>
                  </div>
                )}

                {/* ═══ SOLO SIGN UP TAB ═══ */}
                {activeTab === 'solo' && (
                  <div data-testid="solo-tab-content">
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <Truck className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Solo Plan <span className="text-sm font-normal text-gray-400">$39/mo</span></h3>
                        <p className="text-xs text-gray-500">1 company, unlimited claims, 30-day free trial</p>
                      </div>
                    </div>
                    <RegistrationForm accountType="solo" accentColor="teal" />
                  </div>
                )}

                {/* ═══ PROVIDER SIGN UP TAB ═══ */}
                {activeTab === 'provider' && (
                  <div data-testid="provider-tab-content">
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Network className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Provider Plan <span className="text-sm font-normal text-gray-400">$99/mo</span></h3>
                        <p className="text-xs text-gray-500">6 orgs, 6 companies, multi-org management, 30-day free trial</p>
                      </div>
                    </div>
                    <RegistrationForm accountType="provider" accentColor="blue" />
                  </div>
                )}
              </CardContent>
            </div>
          </div>
        </Card>

        {/* Footer note */}
        <p className="text-center mt-4 text-xs text-gray-400">
          Demos reset on each login &middot; No credit card required for trials
        </p>
      </div>
    </div>
  );
}

const GoogleIcon = () => (
  <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);
